import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { config } from '../../config/config';
import { showToast } from '../../components/utils/Toast';

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const json = async (res) => {
  const data = await res.json();
  if (!res.ok) throw new Error(data.message || 'Request failed');
  return data;
};

export const fetchCrews = createAsyncThunk(
  'crews/fetch',
  async (_, { rejectWithValue }) => {
    try {
      const res = await fetch(`${config.apiUrl}/api/crews`, { headers: authHeader() });
      const data = await json(res);
      return data.crews;
    } catch (e) { return rejectWithValue(e.message); }
  }
);

export const createCrew = createAsyncThunk(
  'crews/create',
  async (payload, { rejectWithValue }) => {
    try {
      const res = await fetch(`${config.apiUrl}/api/crews`, {
        method: 'POST',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const data = await json(res);
      return data;
    } catch (e) { return rejectWithValue(e.message); }
  }
);

export const updateCrew = createAsyncThunk(
  'crews/update',
  async ({ id, updates }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${config.apiUrl}/api/crews/${id}`, {
        method: 'PUT',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      const data = await json(res);
      return data.crew;
    } catch (e) { return rejectWithValue(e.message); }
  }
);

export const deleteCrew = createAsyncThunk(
  'crews/delete',
  async (id, { rejectWithValue }) => {
    try {
      const res = await fetch(`${config.apiUrl}/api/crews/${id}`, {
        method: 'DELETE', headers: authHeader()
      });
      await json(res);
      return id;
    } catch (e) { return rejectWithValue(e.message); }
  }
);

export const setCrewUnavailability = createAsyncThunk(
  'crews/setUnavailability',
  async ({ id, unavailability }, { rejectWithValue }) => {
    try {
      const res = await fetch(`${config.apiUrl}/api/crews/${id}/unavailability`, {
        method: 'PUT',
        headers: { ...authHeader(), 'Content-Type': 'application/json' },
        body: JSON.stringify({ unavailability })
      });
      const data = await json(res);
      return data.crew;
    } catch (e) { return rejectWithValue(e.message); }
  }
);

const slice = createSlice({
  name: 'crews',
  initialState: { items: [], loading: false, error: null },
  reducers: {},
  extraReducers: (b) => {
    b.addCase(fetchCrews.pending, (s) => { s.loading = true; });
    b.addCase(fetchCrews.fulfilled, (s, a) => { s.loading = false; s.items = a.payload; });
    b.addCase(fetchCrews.rejected, (s, a) => { s.loading = false; s.error = a.payload; });
    b.addCase(createCrew.fulfilled, (s, a) => {
      s.items.push(a.payload.crew);
      showToast('Crew created', 'success');
    });
    b.addCase(createCrew.rejected, (_, a) => { showToast(a.payload, 'error'); });
    b.addCase(updateCrew.fulfilled, (s, a) => {
      const i = s.items.findIndex((c) => c._id === a.payload._id);
      if (i >= 0) s.items[i] = a.payload;
      showToast('Crew updated', 'success');
    });
    b.addCase(deleteCrew.fulfilled, (s, a) => {
      s.items = s.items.filter((c) => c._id !== a.payload);
      showToast('Crew deleted', 'success');
    });
    b.addCase(setCrewUnavailability.fulfilled, (s, a) => {
      const i = s.items.findIndex((c) => c._id === a.payload._id);
      if (i >= 0) s.items[i] = a.payload;
      showToast('Availability updated', 'success');
    });
  }
});

export default slice.reducer;
